import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { useCarePlanEvents, useCarePlanSchedule } from '../../utils/reactQueryHooks';
import '../../styles/automation.css';

const AutomationTimeline = ({ plans, actionType }) => {
    // Fetch care plan events and schedule to determine current execution status
    const { data: carePlanEventsRaw = [] } = useCarePlanEvents({
        execution_status: 'executing'
    });
    
    const { data: carePlanScheduleRaw = [] } = useCarePlanSchedule({
        execution_status: 'executing'
    });

    // Ensure we have arrays
    const carePlanEvents = Array.isArray(carePlanEventsRaw) ? carePlanEventsRaw : [];
    const carePlanSchedule = Array.isArray(carePlanScheduleRaw) ? carePlanScheduleRaw : [];

    const getActionIcon = () => {
        const icons = {
            light: 'ri-sun-line',
            irrigation: 'ri-drop-line',
            fan: 'ri-wind-line',
            humidity: 'ri-cloud-line'
        };
        return icons[actionType] || 'ri-settings-line';
    };

    const getActionColor = () => {
        const colors = {
            light: '#ffd700',
            irrigation: '#4fc3f7',
            fan: '#81c784',
            humidity: '#ba68c8'
        };
        return colors[actionType] || '#9e9e9e';
    };

    const formatDate = (dateString) => {
        if (!dateString) return '';
        
        try {
            // Handle YYYY-MM-DD format properly
            const date = new Date(dateString + 'T00:00:00'); // Add time to ensure consistent parsing
            
            // Validate the date is valid
            if (isNaN(date.getTime())) {
                console.error('Invalid date in AutomationTimeline formatDate:', dateString);
                return 'Invalid Date';
            }
            
            return date.toLocaleDateString('en-US', { 
                month: 'short', 
                day: 'numeric' 
            });
        } catch (error) {
            console.error('Error formatting date in AutomationTimeline:', error, dateString);
            return 'Invalid Date';
        }
    };

    const formatTime = (timeString) => {
        const [hours, minutes] = timeString.split(':');
        const hour = parseInt(hours);
        const ampm = hour >= 12 ? 'PM' : 'AM';
        const displayHour = hour % 12 || 12;
        return `${displayHour}:${minutes} ${ampm}`;
    };

    // Determine which step is currently executing
    const getCurrentExecutingStep = useMemo(() => {
        // Check if there are any currently executing events
        const executingEvents = carePlanEvents.filter(event => 
            event.execution_status === 'executing'
        );
        
        // Check if there are any currently executing scheduled items
        const executingSchedule = carePlanSchedule.filter(schedule => 
            schedule.execution_status === 'executing'
        );
        
        // Return the first executing event or schedule item
        return executingEvents[0] || executingSchedule[0] || null;
    }, [carePlanEvents, carePlanSchedule]);

    // Helper function to determine if an event is currently executing
    const isEventCurrentlyExecuting = (event, eventIndex, plan, allEvents) => {
        if (!plan.isActive) return false;
        
        const now = new Date();
        
        if (plan.type === 'seasonal') {
            // For seasonal plans, check if we're within the event's date range
            const eventDate = new Date(event.date + 'T00:00:00');
            const nextEvent = allEvents[eventIndex + 1];
            const planEndDate = plan.endDate ? new Date(plan.endDate + 'T23:59:59') : null;
            
            // If this is the last event, execute until plan end date
            if (!nextEvent) {
                return now >= eventDate && (!planEndDate || now <= planEndDate);
            }
            
            // If there's a next event, execute until that event's start date
            const nextEventDate = new Date(nextEvent.date + 'T00:00:00');
            return now >= eventDate && now < nextEventDate;
            
        } else if (plan.type === 'daily') {
            // For daily plans, check if we're within the event's time window
            const today = new Date();
            const eventTime = event.time;
            const [eventHour, eventMinute] = eventTime.split(':').map(Number);
            const eventDateTime = new Date(today.getFullYear(), today.getMonth(), today.getDate(), eventHour, eventMinute);
            
            const nextEvent = allEvents[eventIndex + 1];
            
            // If this is the last event, execute until end of day
            if (!nextEvent) {
                const endOfDay = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 23, 59, 59);
                return now >= eventDateTime && now <= endOfDay;
            }
            
            // If there's a next event, execute until that event's time
            const nextEventTime = nextEvent.time;
            const [nextEventHour, nextEventMinute] = nextEventTime.split(':').map(Number);
            const nextEventDateTime = new Date(today.getFullYear(), today.getMonth(), today.getDate(), nextEventHour, nextEventMinute);
            
            return now >= eventDateTime && now < nextEventDateTime;
        }
        
        return false;
    };

    const getTimelineEvents = () => {
        const events = [];
        
        // Only process active plans
        const activePlans = plans.filter(plan => plan.isActive);
        console.log('AutomationTimeline - All plans:', plans);
        console.log('AutomationTimeline - Active plans:', activePlans);
        
        activePlans.forEach(plan => {
            if (plan.events) {
                // First, collect all events for this plan to determine execution windows
                const planEvents = plan.events.map((event, eventIndex) => {
                    if (plan.type === 'seasonal' && event.date) {
                        return {
                            ...event,
                            planName: plan.name,
                            planType: 'seasonal',
                            date: event.date,
                            displayValue: event.value,
                            isActive: plan.isActive
                        };
                    } else if (plan.type === 'daily' && event.time) {
                        return {
                            ...event,
                            planName: plan.name,
                            planType: 'daily',
                            time: event.time,
                            displayValue: event.value,
                            description: event.description,
                            isActive: plan.isActive
                        };
                    }
                    return null;
                }).filter(Boolean);
                
                // Now determine which event is currently executing
                planEvents.forEach((event, eventIndex) => {
                    event.isCurrentlyExecuting = isEventCurrentlyExecuting(event, eventIndex, plan, planEvents);
                    events.push(event);
                });
            }
        });
        
        // Sort events by date/time
        return events.sort((a, b) => {
            if (a.planType === 'seasonal' && b.planType === 'seasonal') {
                return new Date(a.date) - new Date(b.date);
            } else if (a.planType === 'daily' && b.planType === 'daily') {
                return a.time.localeCompare(b.time);
            }
            return 0;
        });
    };

    const timelineEvents = getTimelineEvents();

    if (timelineEvents.length === 0) {
        return null;
    }

    // Check if any event is currently executing for the header indicator
    const hasExecutingEvent = timelineEvents.some(event => event.isCurrentlyExecuting);

    return (
        <div className="automation-timeline">
            <div className="timeline-header">
                <h4>
                    <i className={getActionIcon()} style={{ color: getActionColor() }}></i>
                    Scheduled Timeline
                </h4>
                {hasExecutingEvent && (
                    <div className="current-execution-indicator">
                        <i className="ri-play-circle-line"></i>
                        <span>Currently executing a step</span>
                    </div>
                )}
            </div>
            
            <div className="timeline-container">
                {timelineEvents.map((event, index) => (
                    <div 
                        key={index} 
                        className={`timeline-event ${event.isActive ? 'active' : ''} ${event.isCurrentlyExecuting ? 'currently-executing' : ''}`}
                    >
                        <div className="timeline-marker" style={{ backgroundColor: getActionColor() }}>
                            <i className={getActionIcon()}></i>
                            {event.isCurrentlyExecuting && (
                                <div className="execution-indicator">
                                    <i className="ri-play-fill"></i>
                                </div>
                            )}
                        </div>
                        
                        <div className="timeline-content">
                            <div className="event-header">
                                <span className="event-plan">{event.planName}</span>
                                <span className={`event-status ${event.isActive ? 'active' : 'inactive'} ${event.isCurrentlyExecuting ? 'executing' : ''}`}>
                                    {event.isCurrentlyExecuting ? 'Executing' : event.isActive ? 'Active' : 'Inactive'}
                                </span>
                            </div>
                            
                            <div className="event-details">
                                {event.planType === 'seasonal' ? (
                                    <>
                                        <span className="event-date">
                                            <i className="ri-calendar-line"></i>
                                            {formatDate(event.date)}
                                        </span>
                                        <span className="event-value">
                                            <i className="ri-settings-line"></i>
                                            {event.displayValue}
                                        </span>
                                    </>
                                ) : (
                                    <>
                                        <span className="event-time">
                                            <i className="ri-time-line"></i>
                                            {formatTime(event.time)}
                                        </span>
                                        <span className="event-value">
                                            <i className="ri-settings-line"></i>
                                            {event.displayValue}
                                        </span>
                                        {event.description && (
                                            <span className="event-description">
                                                <i className="ri-information-line"></i>
                                                {event.description}
                                            </span>
                                        )}
                                    </>
                                )}
                                {event.isCurrentlyExecuting && (
                                    <span className="execution-status-indicator">
                                        <i className="ri-play-circle-line"></i>
                                        Currently executing
                                    </span>
                                )}
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

AutomationTimeline.propTypes = {
    plans: PropTypes.array.isRequired,
    actionType: PropTypes.string.isRequired
};

export default AutomationTimeline; 